/**
 * EscalationRuleService - Handles escalation-based rule processing
 *
 * Escalation rules intercept consequence creation and upgrade the consequence type
 * when a student has accumulated enough of a particular consequence within a timeframe.
 *
 * Key Differences from Threshold Rules:
 * - Threshold rules CREATE new consequences when counts are met
 * - Escalation rules MODIFY what consequence gets created (upgrade/replace)
 * - Escalation rules intercept BEFORE consequence creation
 * - Escalation rules can "reset after use" to clear tracking
 */

import { phaseRuleAllowsRecord, isPhaseExecutionError, phaseExecutionError } from '../../domain/phaseRuntime';

import { isSchoolRulePeriodError } from '../utils/dateHelpers';

import {
  collection,
  query,
  where,
  getDocs,
  getDocFromServer,
  updateDoc,
  Timestamp,
  arrayUnion,
  addDoc,
  doc
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { getDateRangeForRuleWithDetection } from '../utils/ruleDateHelpers';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { processingGuard } from '../utils/ProcessingGuard';

export class EscalationRuleService {
  constructor(dependencies) {
    this.ruleService = dependencies.ruleService;
    this.typeNormalizer = dependencies.typeNormalizer;
  }

  /** Resolve applicability from the causal record, never the staff filter. */
  async resolveEligibility(studentId, context) {
    let phasePolicy;
    try {
      phasePolicy = await this.ruleService.getPhasePolicy?.(context.schoolId);
    } catch (error) {
      throw phaseExecutionError(error.message);
    }
    let record = context.sourceRecord || context.originalData;
    try {
      if (phasePolicy?.enabled && context.phaseSource) {
        const snapshot = await getDocFromServer(doc(db, 'schools', context.schoolId, context.phaseSource.collection, context.phaseSource.id));
        record = snapshot.exists() ? snapshot.data() : null;
        if (!record) throw phaseExecutionError('The original escalation source is unavailable. Retry the original capture.');
      }
      if (phasePolicy?.enabled && record && (record.studentId !== studentId ||
          (record.schoolId && record.schoolId !== context.schoolId))) {
        throw phaseExecutionError('The original escalation source is unavailable. Retry the original capture.');
      }
    } catch (error) {
      throw phaseExecutionError(error.message);
    }
    return {phasePolicy, record: record || {schoolId: context.schoolId, studentId},
      candidateIds: context.candidateIds};
  }

  /**
   * Check if a consequence should be escalated before creation
   * This is the main entry point - called BEFORE creating a consequence
   * Supports cascading escalations (e.g., friday-detention → saturday-detention → suspension)
   * @param {string} studentId - Student ID
   * @param {string} proposedConsequenceType - The consequence type about to be created
   * @param {Object} context - Additional context (ruleName, ruleId, etc.)
   * @returns {Promise<Object|null>} Escalation result or null
   */
  async checkEscalationBeforeCreation(studentId, proposedConsequenceType, context = {}) {
    let eligibility;
    try {
      // Extract schoolId from context
      const schoolId = context.schoolId;
      if (!schoolId) {
        console.error('[EscalationRuleService] schoolId required in context for hierarchical collections');
        return null;
      }

      eligibility = await this.resolveEligibility(studentId, context);

      disciplineLogger.critical(`🔍 ESCALATION SERVICE CALLED`, {
        studentId,
        proposedType: proposedConsequenceType,
        context
      }, 'escalations');

      // Track the current consequence type as it changes through escalations
      let currentType = proposedConsequenceType;
      const processedTypes = new Set(); // Prevent circular escalations
      const escalationChain = []; // Track all escalations that occur
      let cascadeLevel = 0;
      const MAX_CASCADE_DEPTH = 10; // Safety limit

      // Keep checking for escalations until no more rules trigger
      while (!processedTypes.has(currentType) && cascadeLevel < MAX_CASCADE_DEPTH) {
        processedTypes.add(currentType);
        cascadeLevel++;

        disciplineLogger.info(`🔍 CASCADE LEVEL ${cascadeLevel}: Checking escalations for "${currentType}"`, {
          currentType,
          processedTypes: Array.from(processedTypes),
          chainLength: escalationChain.length
        }, 'escalations');

        // Get all active escalation rules that monitor the CURRENT consequence type
        const escalationRules = await this.getEscalationRulesFor(currentType, schoolId, eligibility?.phasePolicy?.enabled);

        if (escalationRules.length === 0) {
          disciplineLogger.debug(`No escalation rules found for type: ${currentType}`);
          break; // No more escalations possible
        }

        disciplineLogger.info(`⬆️ Found ${escalationRules.length} escalation rules to check for "${currentType}"`, {
          rules: escalationRules.map(r => ({
            title: r.title,
            monitorsType: r.monitorsType,
            threshold: r.threshold,
            escalatesTo: r.escalatesTo
          }))
        }, 'escalations');

        // Check each rule in priority order (highest priority first)
        let escalationOccurred = false;
        for (const rule of escalationRules) {
          // Pass escalation chain so rule can count consequences being created in this cascade
          // CRITICAL: Pass schoolId for hierarchical queries
          const result = await this.evaluateEscalationRule(rule, studentId, currentType, escalationChain, schoolId, context.referenceDate || context.issuedAt, eligibility);

          if (result) {
            disciplineLogger.critical(`⬆️ CASCADE LEVEL ${cascadeLevel}: ESCALATION TRIGGERED: "${rule.title}" [${result.actualCount}/${rule.threshold}]`, {
              from: currentType,
              to: result.escalatedType,
              consumedCount: result.consumedDocs.length,
              foundCount: result.actualCount,
              threshold: rule.threshold,
              willReset: rule.resetAfterUse,
              cascadeLevel
            }, 'escalations');

            // Add to escalation chain
            escalationChain.push({
              ...result,
              fromType: currentType,
              cascadeLevel
            });

            // If reset-after-use is enabled, mark the escalation tracking record
            if (rule.resetAfterUse && !eligibility?.phasePolicy?.enabled) {
              await this.createEscalationTrackingRecord(rule.id, studentId, result);
            }

            // Update current type and continue cascading
            currentType = result.escalatedType;
            escalationOccurred = true;
            break; // Move to next cascade level with the new type
          }
        }

        if (!escalationOccurred) {
          disciplineLogger.debug(`No escalation rules triggered for ${currentType} at level ${cascadeLevel}`);
          break; // No more escalations at this level
        }
      }

      // Check for circular escalation (safety)
      if (cascadeLevel >= MAX_CASCADE_DEPTH) {
        disciplineLogger.critical(`⚠️ MAX CASCADE DEPTH REACHED (${MAX_CASCADE_DEPTH})`, {
          originalType: proposedConsequenceType,
          finalType: currentType,
          processedTypes: Array.from(processedTypes)
        }, 'escalations');
      }

      // Return combined result if any escalations occurred
      if (escalationChain.length > 0) {
        const combinedResult = this.combineEscalationResults(escalationChain, proposedConsequenceType);

        disciplineLogger.critical(`✅ ESCALATION CHAIN COMPLETE`, {
          originalType: proposedConsequenceType,
          finalType: combinedResult.escalatedType,
          chainLength: escalationChain.length,
          escalationPath: escalationChain.map(e => `${e.fromType} → ${e.escalatedType}`).join(' → '),
          totalConsumed: combinedResult.consumedDocs.length
        }, 'escalations');

        return {...combinedResult, atomicPersistence: eligibility?.phasePolicy?.enabled === true};
      }

      disciplineLogger.debug(`No escalation rules triggered for ${proposedConsequenceType}`);
      return null;

    } catch (error) {

      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      if (eligibility?.phasePolicy?.enabled) throw phaseExecutionError(error.message);
      console.error('Error checking escalation before creation:', error);
      return null; // Don't block consequence creation on escalation errors
    }
  }

  /**
   * Check if a consequence should be escalated AFTER creation (transaction-based)
   * This method is called within a transaction after the consequence is created
   * It counts existing consequences INCLUDING the one just created
   * @param {Object} transaction - Firestore transaction
   * @param {string} studentId - Student ID
   * @param {string} consequenceType - The type of consequence created
   * @param {string} consequenceId - The ID of the created consequence
   * @param {Object} context - Additional context
   * @returns {Promise<Object|null>} Escalation result or null if no escalation
   */
  async checkEscalationAfterCreation(transaction, studentId, consequenceType, consequenceId, context = {}) {
    let currentType = consequenceType;
    const processedTypes = new Set();
    const escalationChain = [];
    let cascadeLevel = 0;
    const MAX_CASCADE_DEPTH = 10;

    // CRITICAL: Extract schoolId from context
    const schoolId = context.schoolId;
    if (!schoolId) {
      disciplineLogger.critical('❌ Cannot check escalation: schoolId missing in context', {
        studentId,
        consequenceType,
        consequenceId
      });
      return null;
    }

    const eligibility = await this.resolveEligibility(studentId, context);

    disciplineLogger.info('🔍 Checking escalation AFTER creation', {
      studentId,
      consequenceType,
      consequenceId,
      schoolId,
      maxDepth: MAX_CASCADE_DEPTH
    }, 'escalations');

    while (!processedTypes.has(currentType) && cascadeLevel < MAX_CASCADE_DEPTH) {
      processedTypes.add(currentType);
      cascadeLevel++;

      const escalationRules = await this.getEscalationRulesFor(currentType, schoolId, eligibility?.phasePolicy?.enabled);

      if (escalationRules.length === 0) {
        disciplineLogger.debug(`No escalation rules for ${currentType}`, null, 'escalations');
        break;
      }

      let escalationOccurred = false;

      for (const rule of escalationRules) {
        // Evaluate the rule WITH the newly created consequence included
        const result = await this.evaluateEscalationRuleInTransaction(
          transaction,
          rule,
          studentId,
          currentType,
          consequenceId,
          escalationChain,
          schoolId,
          context.referenceDate || context.issuedAt,
          eligibility
        );

        if (result) {
          escalationChain.push({
            ...result,
            fromType: currentType,
            cascadeLevel
          });

          currentType = result.escalatedType;
          escalationOccurred = true;

          disciplineLogger.info(`✓ Escalation ${cascadeLevel}: ${result.fromType} → ${result.escalatedType}`, {
            ruleId: rule.id,
            ruleTitle: rule.title
          }, 'escalations');

          break; // Move to next cascade level
        }
      }

      if (!escalationOccurred) {
        break;
      }
    }

    if (escalationChain.length > 0) {
      return this.combineEscalationResults(escalationChain, consequenceType);
    }

    return null;
  }

  /**
   * Evaluate an escalation rule within a transaction
   * @param {Object} transaction - Firestore transaction
   * @param {Object} rule - The escalation rule to evaluate
   * @param {string} studentId - Student ID
   * @param {string} currentType - Current consequence type
   * @param {string} newConsequenceId - The ID of the newly created consequence
   * @param {Array} escalationChain - Current escalation chain
   * @param {string} schoolId - School ID for hierarchical queries
   * @returns {Promise<Object|null>} Escalation result or null
   */
  async evaluateEscalationRuleInTransaction(transaction, rule, studentId, currentType, newConsequenceId, _escalationChain = [], schoolId, referenceDate = new Date(), eligibility = {}) {
    // CRITICAL: Validate schoolId is provided
    if (!schoolId) {
      console.error('[EscalationRuleService] schoolId is required for evaluateEscalationRuleInTransaction');
      return null;
    }

    const phasePolicy = eligibility.phasePolicy ?? await this.ruleService.getPhasePolicy?.(schoolId);
    const trigger = eligibility.record || {schoolId, studentId};
    if (!phaseRuleAllowsRecord(phasePolicy, rule, trigger)) return null;

    const { monitorsType, escalatesTo, threshold } = rule;

    // The rule should only trigger if we're monitoring the current type
    if (monitorsType !== currentType) {
      return null;
    }

    disciplineLogger.info(`⬆️ Evaluating escalation rule "${rule.title}" in transaction`, {
      ruleId: rule.id,
      monitorsType,
      escalatesTo,
      threshold,
      studentId,
      newConsequenceId
    }, 'escalations');

    const dateRange = await getDateRangeForRuleWithDetection(rule, rule.timeframe || 'week', rule.timeCount || 1, schoolId, { referenceDate });

    // Query for existing consequences of this type
    // INCLUDING the one we just created
    // CRITICAL: Use hierarchical path
    const q = query(
      collection(db, 'schools', schoolId, 'disciplineQueue'),
      where('studentId', '==', studentId),
      where('type', '==', monitorsType),
      where('undone', '==', false)
    );

    const snapshot = await getDocs(q);
    processingGuard.trackReads('EscalationService.queryConsequences', snapshot.size, {
      studentId,
      consequenceType: currentType,
      ruleId: rule.id
    });

    // Filter to get unused consequences
    const unusedDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      const issued = data.issuedAt?.toDate?.() || new Date(data.issuedAt || data.createdAt);
      return !data.usedByThresholdRule && !data.usedByEscalationRule
        && (!eligibility.candidateIds || eligibility.candidateIds.includes(doc.id))
        && issued >= dateRange.start && issued <= dateRange.end
        && phaseRuleAllowsRecord(phasePolicy, rule, data, {historical: true});
    });

    // Count includes the newly created consequence
    const count = unusedDocs.length;

    disciplineLogger.info(`⬆️ Found ${count}/${threshold} ${monitorsType} (including new)`, {
      totalFound: snapshot.docs.length,
      unusedCount: count,
      threshold,
      newConsequenceId
    }, 'escalations');

    // Check if threshold is met
    // IMPORTANT: count now INCLUDES the newly created consequence
    // For "After 2, escalate the 3rd", when count=3 (including new), we escalate
    // So we need count > threshold, not count >= threshold
    if (count > threshold) {
      disciplineLogger.critical(`🎯 ESCALATION THRESHOLD MET (after creation)`, {
        count,
        threshold,
        reason: `Student has ${count} ${monitorsType} (including new), threshold is ${threshold} - escalating`
      }, 'escalations');

      // Get the docs to consume (but not the one being escalated!)
      const docsToConsume = unusedDocs
        .filter(doc => doc.id !== newConsequenceId) // Don't consume the one we're escalating
        .slice(0, threshold); // Take up to threshold number

      return {
        escalatedType: escalatesTo,
        consumedDocs: docsToConsume,
        escalationRuleId: rule.id,
        expectedRuleRevision: rule.configurationRevision ?? 0,
        escalationRuleTitle: rule.title,
        resetAfterUse: rule.resetAfterUse,
        actualCount: count
      };
    }

    return null;
  }

  /**
   * Combine multiple escalation results from a cascade chain
   * @param {Array} escalationChain - Array of escalation results
   * @param {string} originalType - The original consequence type before any escalations
   * @returns {Object} Combined escalation result
   */
  combineEscalationResults(escalationChain, originalType) {
    if (escalationChain.length === 0) return null;

    // The final escalation in the chain determines the actual consequence type
    const finalEscalation = escalationChain[escalationChain.length - 1];

    // Collect all consumed documents from all levels
    const allConsumedDocs = [];
    const allRuleIds = [];
    const allRuleTitles = [];

    for (const escalation of escalationChain) {
      allConsumedDocs.push(...escalation.consumedDocs);
      allRuleIds.push(escalation.escalationRuleId);
      allRuleTitles.push(escalation.escalationRuleTitle);
    }

    // Build the escalation path for logging/tracking
    const escalationPath = escalationChain.map(e =>
      `${e.fromType} → ${e.escalatedType} (via "${e.escalationRuleTitle}")`
    );

    return {
      escalatedType: finalEscalation.escalatedType,
      consumedDocs: allConsumedDocs,
      escalationRuleId: allRuleIds, // Now an array of all rules that fired
      escalationRuleTitle: allRuleTitles, // Array of all rule titles
      resetAfterUse: escalationChain.some(e => e.resetAfterUse), // True if any rule resets
      cascadeChain: escalationChain, // Full chain details
      escalationPath: escalationPath, // Human-readable path
      originalType: originalType, // Track where we started
      cascadeLevels: escalationChain.length
    };
  }

  /**
   * Get escalation rules that monitor a specific consequence type
   * @param {string} consequenceType - Type of consequence (e.g., 'friday-detention')
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Escalation rules sorted by priority
   */
  async getEscalationRulesFor(consequenceType, schoolId, strict = false) {
    try {
      if (!schoolId) {
        throw new Error('schoolId is required for hierarchical disciplineRules collection');
      }

      const activeRules = await this.ruleService.getActiveRules(schoolId);

      // Use TypeNormalizer for consistent type matching
      const normalizedConsequenceType = await this.typeNormalizer.normalize(consequenceType, schoolId);

      const escalationRules = [];
      for (const r of activeRules) {
        if (r.type !== 'escalation') continue;

        const normalizedMonitorsType = await this.typeNormalizer.normalize(r.monitorsType, schoolId);
        const monitorsType = normalizedMonitorsType === normalizedConsequenceType;

        disciplineLogger.debug(`Checking rule: "${r.title}"`, {
          monitorsType: r.monitorsType,
          normalized: normalizedMonitorsType,
          consequenceType,
          normalizedConsequence: normalizedConsequenceType,
          matches: monitorsType
        }, 'escalations');

        if (monitorsType) {
          escalationRules.push(r);
        }
      }

      disciplineLogger.debug(`Found ${escalationRules.length} escalation rules for ${consequenceType}`, {
        rules: escalationRules.map(r => r.title)
      }, 'escalations');

      // Sort by priority (higher priority first)
      return escalationRules.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      if (strict) throw phaseExecutionError(error.message);
      console.error('Error fetching escalation rules:', error);
      return [];
    }
  }

  /**
   * Evaluate a single escalation rule
   * @param {Object} rule - The escalation rule
   * @param {string} studentId - Student ID
   * @param {string} proposedConsequenceType - The consequence type being created
   * @param {Array} escalationChain - Previous escalations in current cascade (for counting)
   * @param {string} schoolId - School ID for hierarchical queries
   * @returns {Promise<Object|null>} Escalation result or null
   */
  async evaluateEscalationRule(rule, studentId, proposedConsequenceType, escalationChain = [], schoolId, referenceDate = new Date(), eligibility = {}) {
    // CRITICAL: Validate schoolId is provided
    if (!schoolId) {
      console.error('[EscalationRuleService] schoolId is required for evaluateEscalationRule');
      return null;
    }

    const phasePolicy = eligibility.phasePolicy ?? await this.ruleService.getPhasePolicy?.(schoolId);
    const trigger = eligibility.record || {schoolId, studentId};
    if (!phaseRuleAllowsRecord(phasePolicy, rule, trigger)) return null;

    const threshold = rule.threshold || 0;
    const timeframe = rule.timeframe || 'weekly';
    const escalatesTo = rule.escalatesTo;

    // Use TypeNormalizer for consistent type matching
    const normalizedMonitorsType = await this.typeNormalizer.normalize(rule.monitorsType, schoolId);

    disciplineLogger.info(`⬆️ 📊 EVALUATING: "${rule.title}"`, {
      ruleId: rule.id,
      monitorsType: rule.monitorsType,
      normalizedMonitorsType,
      threshold,
      escalatesTo,
      timeframe,
      resetAfterUse: rule.resetAfterUse,
      cascadeChainLength: escalationChain.length
    }, 'escalations');

    const dateRange = await getDateRangeForRuleWithDetection(rule, timeframe, rule.timeCount || 1, schoolId, { referenceDate });

    // Query for qualifying consequences - prefer typeId, fallback to type string
    const typeId = await this.typeNormalizer.normalizeToId(rule.monitorsType, schoolId);

    let q;
    if (typeId) {
      // Preferred: Query by typeId (new records)
      disciplineLogger.debug(`Escalation querying by typeId: ${rule.monitorsType} → ${typeId}`, {
        original: rule.monitorsType,
        typeId: typeId
      }, 'escalations');

      // CRITICAL: Use hierarchical path
      q = query(
        collection(db, 'schools', schoolId, 'disciplineQueue'),
        where('studentId', '==', studentId),
        where('typeId', '==', typeId),
        where('undone', '==', false),
        where('issuedAt', '>=', Timestamp.fromDate(dateRange.start))
      );
    } else {
      // Fallback: Query by string type (legacy records)
      disciplineLogger.debug(`Escalation querying by type string: ${rule.monitorsType} → ${normalizedMonitorsType}`, {
        original: rule.monitorsType,
        normalized: normalizedMonitorsType
      }, 'escalations');

      // CRITICAL: Use hierarchical path
      q = query(
        collection(db, 'schools', schoolId, 'disciplineQueue'),
        where('studentId', '==', studentId),
        where('type', '==', normalizedMonitorsType),
        where('undone', '==', false),
        where('issuedAt', '>=', Timestamp.fromDate(dateRange.start))
      );
    }

    const snapshot = await getDocs(q);
    processingGuard.trackReads('EscalationService.evaluateRule', snapshot.size, {
      studentId,
      monitorsType: rule.monitorsType,
      ruleId: rule.id
    });

    // Filter out consequences already consumed or used
    const unusedDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      const issued = data.issuedAt?.toDate?.() || new Date(data.issuedAt || data.createdAt);
      return !data.usedByThresholdRule && !data.usedByEscalationRule
        && (!eligibility.candidateIds || eligibility.candidateIds.includes(doc.id))
        && issued >= dateRange.start && issued <= dateRange.end
        && phaseRuleAllowsRecord(phasePolicy, rule, data, {historical: true});
    });

    // Count database records but DON'T count consequences from the cascade chain
    // The cascade chain represents consequences being created in THIS cascade operation
    // They haven't been created yet, so they shouldn't count toward the threshold
    //
    // Example: When checking if a Sat DT should escalate to Suspension:
    // - Database might have 1 Sat DT (from a previous, completed escalation)
    // - Cascade chain has 1 Sat DT (being created RIGHT NOW from FDT escalation)
    // - We should only count the 1 from database, not the one being created now
    // - The Sat DT being created now will only count for FUTURE escalation checks
    const databaseCount = unusedDocs.length;
    const count = databaseCount;

    disciplineLogger.info(`⬆️ ✓ Found ${count}/${threshold} unused ${rule.monitorsType}`, {
      totalFound: snapshot.docs.length,
      databaseUnused: databaseCount,
      totalCount: count,
      consumedCount: snapshot.docs.length - databaseCount
    }, 'escalations');

    // Check if threshold is met
    // The rule "After N consequences, the next will escalate" means:
    // When the student already has N consequences, the NEXT one (N+1) escalates
    // Example: "After 2 friday-detentions" means when they have 2, the 3rd one escalates
    if (count >= threshold) {
      disciplineLogger.critical(`🎯 ESCALATION THRESHOLD MET`, {
        existingCount: count,
        totalWithCurrent: count + 1,
        threshold: threshold,
        reason: `Creating consequence #${count + 1} which exceeds threshold of ${threshold} - will escalate`
      });

      // Consume the first 'threshold' consequences if resetAfterUse is enabled
      // Example: "After 2" means consume those 2 consequences that triggered the escalation
      const docsToConsume = unusedDocs.slice(0, threshold);

      return {
        escalatedType: escalatesTo,
        consumedDocs: docsToConsume,
        escalationRuleId: rule.id,
        expectedRuleRevision: rule.configurationRevision ?? 0,
        escalationRuleTitle: rule.title,
        resetAfterUse: rule.resetAfterUse,
        actualCount: count
      };
    }

    disciplineLogger.debug(`Threshold not met: ${count} < ${threshold}`);
    return null;
  }

  /**
   * Mark consequences as consumed by escalation rule
   * @param {Array} docs - Document snapshots
   * @param {string} ruleId - Escalation rule ID
   * @returns {Promise<void>}
   */
  async markConsequencesAsConsumed(docs, ruleId) {
    disciplineLogger.info(`🔄 CONSUMING ${docs.length} consequences`, {
      ruleId,
      docs: docs.map(d => ({ id: d.id, type: typeof d.data === 'function' ? d.data().type : d.type }))
    });

    for (const docItem of docs) {
      try {
        // Handle both real Firestore documents and plain objects
        let docRef;
        let dataObj;

        if (docItem.ref) {
          // It's a real Firestore document snapshot
          docRef = docItem.ref;
          dataObj = docItem.data();
        } else if (docItem.id) {
          // It's a plain object with an id - need to create a document reference
          // Determine the collection based on the data
          dataObj = typeof docItem.data === 'function' ? docItem.data() : docItem;
          const collectionName = dataObj.mentionType ? 'mentions' : 'disciplineQueue';
          docRef = doc(db, collectionName, docItem.id);
        } else {
          console.error('Invalid document format in markConsequencesAsConsumed:', docItem);
          continue;
        }

        const updateData = {
          usedByEscalationRule: ruleId,
          usedByEscalationAt: Timestamp.now(),
          actioned: true,
          actionedBy: arrayUnion(ruleId),
          actionedAt: Timestamp.now()
        };

        // Update Firestore FIRST
        await updateDoc(docRef, updateData);

        // ONLY update in-memory object if Firestore update succeeded
        // This ensures consistency - if Firestore fails, memory stays unchanged
        if (dataObj) {
          Object.assign(dataObj, {
            usedByEscalationRule: ruleId,
            usedByEscalationAt: updateData.usedByEscalationAt,
            actioned: true,
            actionedBy: [...(dataObj.actionedBy || []), ruleId],
            actionedAt: updateData.actionedAt
          });

          disciplineLogger.debug(`✅ Marked doc ${docItem.id} as consumed in BOTH memory and Firestore`, {
            docId: docItem.id,
            ruleId,
            collection: dataObj.mentionType ? 'mentions' : 'disciplineQueue'
          });
        }
      } catch (error) {
        if (isSchoolRulePeriodError(error)) throw error;
        // DO NOT update in-memory if Firestore update failed!
        console.error(`Failed to mark document ${docItem.id} as consumed in Firestore (memory unchanged):`, error);
        disciplineLogger.critical(`⚠️ SYNC WARNING: Doc ${docItem.id} NOT marked as consumed due to Firestore error`, {
          docId: docItem.id,
          error: error.message,
          willRetry: true
        });
        // Continue processing other documents even if one fails
      }
    }

    disciplineLogger.info(`✓ Consequences marked as consumed`);
  }

  /**
   * Create tracking record for reset-after-use escalations
   * This allows us to clear/reset the escalation after it's been used
   * @param {string} ruleId - Escalation rule ID
   * @param {string} studentId - Student ID
   * @param {Object} escalationResult - Escalation result data
   * @returns {Promise<void>}
   */
  async createEscalationTrackingRecord(ruleId, studentId, escalationResult) {
    try {
      disciplineLogger.info(`📝 Creating escalation tracking record`, {
        ruleId,
        studentId,
        resetAfterUse: true
      });

      // Extract schoolId from the consumed documents (consequences have schoolId)
      const schoolId = escalationResult.consumedDocs[0]?.data?.().schoolId || null;

      const trackingRecord = {
        ruleId,
        studentId,
        schoolId,
        escalatedFrom: escalationResult.consumedDocs[0]?.data?.().type || 'unknown',
        escalatedTo: escalationResult.escalatedType,
        consumedConsequences: escalationResult.consumedDocs.map(d => d.id),
        triggeredAt: Timestamp.now(),
        resetPending: true, // Will be cleared when consequences are consumed
        createdAt: Timestamp.now()
      };

      // CRITICAL: Use hierarchical path for escalationTracking
      if (schoolId) {
        await addDoc(collection(db, 'schools', schoolId, 'escalationTracking'), trackingRecord);
        disciplineLogger.info(`✓ Tracking record created`);
      } else {
        console.warn('[EscalationRuleService] No schoolId found in consequences - skipping tracking record');
      }
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error creating escalation tracking record:', error);
      // Don't throw - this is just for tracking
    }
  }

  /**
   * Process reset-after-use logic after escalated consequence is created
   * This clears consumed consequences so they don't trigger escalation again
   * @param {string} ruleId - Escalation rule ID
   * @param {string} studentId - Student ID
   * @param {Array} consumedDocs - Documents that were consumed
   * @returns {Promise<void>}
   */
  async processResetAfterUse(ruleId, studentId, consumedDocs) {
    try {
      // Extract schoolId from consumed documents
      const schoolId = consumedDocs[0]?.data?.().schoolId || null;
      if (!schoolId) {
        console.warn('[EscalationRuleService] No schoolId found in consumedDocs - cannot process reset');
        return;
      }

      disciplineLogger.info(`🔄 PROCESSING RESET AFTER USE`, {
        ruleId,
        studentId,
        consumedCount: consumedDocs.length
      });

      // Mark consequences as consumed (this prevents them from triggering again)
      await this.markConsequencesAsConsumed(consumedDocs, ruleId);

      // Update tracking record - CRITICAL: Use hierarchical path
      const trackingQuery = query(
        collection(db, 'schools', schoolId, 'escalationTracking'),
        where('ruleId', '==', ruleId),
        where('studentId', '==', studentId),
        where('resetPending', '==', true)
      );

      const trackingSnapshot = await getDocs(trackingQuery);
      processingGuard.trackReads('EscalationService.processReset', trackingSnapshot.size, {
        ruleId,
        studentId
      });

      for (const doc of trackingSnapshot.docs) {
        await updateDoc(doc.ref, {
          resetPending: false,
          resetAt: Timestamp.now()
        });
      }

      disciplineLogger.critical(`✓ RESET COMPLETE`, {
        ruleId,
        consumedCount: consumedDocs.length
      });

    } catch (error) {

      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error processing reset after use:', error);
      // Don't throw - this is cleanup logic
    }
  }

  /**
   * Get escalation history for a student
   * Useful for debugging and UI display
   * @param {string} studentId - Student ID
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Escalation history
   */
  async getEscalationHistory(studentId, schoolId) {
    try {
      if (!schoolId) {
        console.warn('[EscalationRuleService] schoolId required for getEscalationHistory');
        return [];
      }

      // CRITICAL: Use hierarchical path
      const historyQuery = query(
        collection(db, 'schools', schoolId, 'escalationTracking'),
        where('studentId', '==', studentId)
      );

      const snapshot = await getDocs(historyQuery);
      processingGuard.trackReads('EscalationService.getHistory', snapshot.size, {
        studentId
      });
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error getting escalation history:', error);
      return [];
    }
  }
}
